import {
  initVersionedSchema,
  toMentionContent,
  toTextContent,
} from '@principle-theorem/editor';
import {
  IDestinationEntityJobRunOptions,
  IGetRecordResponse,
  IHasSourceIdentifier,
  ITranslationMap,
  type FailedDestinationEntityRecord,
  type IDestinationEntity,
  type IDestinationEntityRecord,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  Interaction,
  stafferToNamedDoc,
  toMention,
} from '@principle-theorem/principle-core';
import {
  InteractionType,
  MentionResourceType,
  type IInteractionV2,
  type IPatient,
  type IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  WithPrimaryKey,
  resolveSequentially,
  snapshotCombineLatest,
  toMomentTz,
  toTimestamp,
  type WithRef,
} from '@principle-theorem/shared';
import { compact } from 'lodash';
import { Observable, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import {
  BasePatientInteractionDestinationEntity,
  IBasePatientInteractionJobData,
  IPatientInteractionMigrationData,
} from '../../../destination/entities/patient-interactions';
import { STAFFER_RESOURCE_TYPE } from '../../../destination/entities/staff';
import {
  ITEM_CODE_NOTE_TO_INTERACTION_MAP,
  ItemCodeNoteType,
} from '../../../mappings/item-codes-to-notes-xlsx';
import { TranslationMapHandler } from '../../../translation-map';
import { PatientDestinationEntity } from './patient';
import {
  PatientSourceEntity,
  PracticeWorksPatient,
} from '../../source/entities/patients';
import { PatientNotesSourceEntity } from '../../../exact/source/entities/patient-notes';
import {
  ExactTreatment,
  PatientTreatmentSourceEntity,
} from '../../../exact/source/entities/patient-treatments';
import { PracticeWorksStafferMappingHandler } from '../mappings/staff';
import { ExactItemCodeToNoteMappingHandler } from '../../../exact/destination/mappings/item-code-to-notes';
import { resolveExactStaffer } from '../../../exact/destination/mappings/staff';

export interface IPatientInteractionJobData
  extends IBasePatientInteractionJobData<PracticeWorksPatient> {
  staff: WithPrimaryKey<ITranslationMap<IStaffer>>[];
  itemCodesToNotes: WithPrimaryKey<ITranslationMap<object, ItemCodeNoteType>>[];
}

export class PatientInteractionsDestinationEntity extends BasePatientInteractionDestinationEntity<
  PracticeWorksPatient,
  IPatientInteractionJobData
> {
  patientSourceEntity = new PatientSourceEntity();

  override sourceEntities = {
    patients: new PatientSourceEntity(),
    notes: new PatientNotesSourceEntity(),
    treatments: new PatientTreatmentSourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
  };

  customMappings = {
    staff: new PracticeWorksStafferMappingHandler(),
    itemCodesToNotes: new ExactItemCodeToNoteMappingHandler(),
  };

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IPatientInteractionJobData[]> {
    const staff$ = combineLatest([
      this.customMappings.staff.getRecords(translationMap),
      translationMap.getByType<IStaffer>(STAFFER_RESOURCE_TYPE),
    ]).pipe(map(([staff, mappedStaff]) => [...staff, ...mappedStaff]));
    const itemCodesToNotes$ =
      this.customMappings.itemCodesToNotes.getRecords(translationMap);

    return snapshotCombineLatest([staff$, itemCodesToNotes$]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.sourceEntities.patients,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(([patients, [staff, itemCodesToNotes]]) =>
        patients.map((sourcePatient) => ({
          sourcePatient,
          staff,
          itemCodesToNotes,
        }))
      )
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IPatientInteractionJobData
  ): Promise<
    | IPatientInteractionMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
  > {
    const sourcePatientId = data.sourcePatient.data.id;
    const patientRef = await translationMap.getDestination<IPatient>(
      sourcePatientId,
      PATIENT_RESOURCE_TYPE
    );

    if (!patientRef) {
      return this._buildErrorResponse(
        data.sourcePatient.record,
        `Couldn't resolve patient`
      );
    }

    const patientNotes = await this.sourceEntities.notes.filterRecords(
      migration,
      'patientId',
      sourcePatientId
    );

    const interactions = await resolveSequentially(
      patientNotes,
      async (note) => {
        const staffer = await resolveExactStaffer(
          note.data.user_id ?? '',
          translationMap,
          data.staff
        );

        const sourceIdentifier = this.sourceEntities.notes.getSourceRecordId(
          note.data
        );
        const pinned = note.data.is_pop_up && !note.data.is_dismissed;

        return {
          sourceIdentifier,
          ...Interaction.init({
            type: InteractionType.Note,
            owner: staffer ? stafferToNamedDoc(staffer) : undefined,
            title: [
              staffer
                ? toMentionContent(
                    toMention(staffer, MentionResourceType.Staffer)
                  )
                : toTextContent('Unknown user'),
              toTextContent(` added a Note`),
            ],
            pinned,
            createdAt: note.record.translations.entryDate,
            content: initVersionedSchema(note.data.note || []),
          }),
        };
      }
    );

    const patientNoteTreatments =
      await this.sourceEntities.treatments.filterRecords(
        migration,
        'patientId',
        sourcePatientId,
        (record) =>
          !!data.itemCodesToNotes.find(
            (mappedNoteCode) =>
              mappedNoteCode.sourceIdentifier === record.data.service_code
          ) && !!record.data.treatment_notes
      );

    const interactionsFromTreatments =
      await this._buildInteractionsFromTreatment(
        patientNoteTreatments,
        data,
        migration,
        translationMap
      );

    return {
      patientRef,
      interactions: compact([...interactions, ...interactionsFromTreatments]),
    };
  }

  private async _buildInteractionsFromTreatment(
    patientNoteTreatments: IGetRecordResponse<ExactTreatment>[],
    data: IPatientInteractionJobData,
    migration: WithRef<IPracticeMigration>,
    translationMap: TranslationMapHandler
  ): Promise<(IInteractionV2 & IHasSourceIdentifier)[]> {
    const interactions = await resolveSequentially(
      patientNoteTreatments,
      async (treatment) => {
        const mappedNote = data.itemCodesToNotes.find(
          (mappedNoteCode) =>
            mappedNoteCode.sourceIdentifier === treatment.data.service_code
        );
        if (
          !mappedNote?.destinationValue ||
          [
            ItemCodeNoteType.Omit,
            ItemCodeNoteType.ClinicalNote,
            ItemCodeNoteType.PatientSocialNote,
            ItemCodeNoteType.SterilisationRecord,
          ].includes(mappedNote.destinationValue.value)
        ) {
          return;
        }

        const staffer = await resolveExactStaffer(
          treatment.data.provider_code ?? '',
          translationMap,
          data.staff
        );
        const sourceIdentifier =
          this.sourceEntities.treatments.getSourceRecordId(treatment.data);
        const type =
          ITEM_CODE_NOTE_TO_INTERACTION_MAP[mappedNote.destinationValue.value];
        const createdAt = treatment.record.translations.plannedDate
          ? toTimestamp(
              toMomentTz(
                treatment.record.translations.plannedDate,
                migration.configuration.timezone
              )
            )
          : undefined;
        const treatmentContent = treatment.data.treatment_notes ?? '';

        return {
          sourceIdentifier,
          ...Interaction.init({
            type,
            owner: staffer ? stafferToNamedDoc(staffer) : undefined,
            title: [
              staffer
                ? toMentionContent(
                    toMention(staffer, MentionResourceType.Staffer)
                  )
                : toTextContent('Unknown user'),
              toTextContent(` added a ${type} note`),
            ],
            createdAt,
            content: initVersionedSchema(treatmentContent || []),
          }),
        };
      }
    );

    return compact(interactions);
  }
}
